import React from 'react';
import { motion } from 'framer-motion';
import useComparisonStore from '../store/comparisonStore';
import { contentVariants } from '../utils/animations';
import { formatPrice } from '../utils/formatters';

/**
 * PriceSelector component
 * Allows users to select a price range for filtering packages
 * Updated to use Zustand store instead of React Context
 */
const PriceSelector = () => {
  const {
    priceRange,
    setPriceRange,
    openComparisonModal,
    isComparisonModalOpen
  } = useComparisonStore();
  
  const handlePriceChange = (e) => {
    setPriceRange([priceRange[0], parseInt(e.target.value)]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  const handleMinPriceChange = (e) => {
    setPriceRange([parseInt(e.target.value), priceRange[1]]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  // Predefined price points for quick selection
  const pricePoints = [
    { label: 'Under R500', min: 0, max: 500 },
    { label: 'R500 - R750', min: 500, max: 750 },
    { label: 'R750 - R1000', min: 750, max: 1000 },
    { label: 'R1000 - R1500', min: 1000, max: 1500 },
    { label: 'Over R1500', min: 1500, max: 2000 }
  ];
  
  const selectPricePoint = (min, max) => {
    setPriceRange([min, max]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  return (
    <motion.div 
      className="space-y-4"
      variants={contentVariants}
    >
      <h3 className="text-sm font-medium text-white">Price Range</h3>
      
      {/* Quick price selection buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {pricePoints.map((point, index) => (
          <motion.button
            key={index}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              priceRange[0] === point.min && priceRange[1] === point.max
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            onClick={() => selectPricePoint(point.min, point.max)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {point.label}
          </motion.button>
        ))}
      </div>
      
      {/* Custom price range sliders */}
      <div className="space-y-4">
        {/* Min price slider */}
        <div className="space-y-2">
          <div className="flex justify-between mb-1">
            <label className="text-xs font-medium text-gray-300">Minimum Price</label>
            <span className="text-xs text-gray-400">
              {formatPrice(priceRange[0])}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max={priceRange[1]}
            step="50"
            value={priceRange[0]}
            onChange={handleMinPriceChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
        </div>
        
        {/* Max price slider */}
        <div className="space-y-2">
          <div className="flex justify-between mb-1">
            <label className="text-xs font-medium text-gray-300">Maximum Price</label>
            <span className="text-xs text-gray-400">
              {priceRange[1] === 2000 ? 'R2000+' : formatPrice(priceRange[1])}
            </span>
          </div>
          <input
            type="range"
            min={priceRange[0]}
            max="2000"
            step="50"
            value={priceRange[1]}
            onChange={handlePriceChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
        </div>
      </div>
      
      {/* Current price range display */}
      <div className="bg-gray-800 rounded-lg p-3 flex justify-between items-center">
        <span className="text-sm text-white font-medium">Current Range:</span>
        <span className="text-sm text-purple-400">
          {formatPrice(priceRange[0])} - {priceRange[1] === 2000 ? 'R2000+' : formatPrice(priceRange[1])}
        </span>
      </div>
    </motion.div>
  );
};

export default PriceSelector;